import { Controller } from "@hotwired/stimulus"

// Toggles Food sold (and similar) reports between month and day date inputs.
// Enforces max day span client-side; server remains the authority.
export default class extends Controller {
  static targets = [
    "checkbox",
    "monthFields",
    "dayFields",
    "monthStart",
    "monthEnd",
    "dayStart",
    "dayEnd",
    "hint"
  ]
  static values = { maxDays: { type: Number, default: 31 } }

  connect() {
    this.sync()
  }

  toggle() {
    this.sync({ seedFromOther: true })
  }

  enforceMaxDays() {
    if (!this.checkboxTarget.checked) return

    const start = this.parseDate(this.dayStartTarget.value)
    const end = this.parseDate(this.dayEndTarget.value)
    if (!start || !end) return

    const maxMs = (this.maxDaysValue - 1) * 24 * 60 * 60 * 1000
    if (end < start) {
      this.dayEndTarget.value = this.formatDate(start)
      return
    }

    if (end - start > maxMs) {
      const clamped = new Date(start.getTime() + maxMs)
      this.dayEndTarget.value = this.formatDate(clamped)
    }
  }

  sync({ seedFromOther = false } = {}) {
    const byDay = this.checkboxTarget.checked

    this.monthFieldsTarget.classList.toggle("hidden", byDay)
    this.dayFieldsTarget.classList.toggle("hidden", !byDay)

    this.monthStartTarget.disabled = byDay
    this.monthEndTarget.disabled = byDay
    this.dayStartTarget.disabled = !byDay
    this.dayEndTarget.disabled = !byDay

    if (seedFromOther) {
      if (byDay) {
        this.seedDayFromMonth()
      } else {
        this.seedMonthFromDay()
      }
    }

    if (byDay) this.enforceMaxDays()

    if (this.hasHintTarget) {
      this.hintTarget.textContent = byDay
        ? "Day view: date range max 31 days."
        : "Month columns by default. Enable day view for daily columns (max 31 days)."
    }
  }

  // Last maxDays calendar days ending yesterday (inclusive span).
  seedDayFromMonth() {
    const end = new Date()
    end.setHours(0, 0, 0, 0)
    end.setDate(end.getDate() - 1)

    const start = new Date(end)
    start.setDate(start.getDate() - (this.maxDaysValue - 1))

    this.dayStartTarget.value = this.formatDate(start)
    this.dayEndTarget.value = this.formatDate(end)
  }

  seedMonthFromDay() {
    const dayStart = this.dayStartTarget.value
    const dayEnd = this.dayEndTarget.value
    if (dayStart) this.monthStartTarget.value = dayStart.slice(0, 7)
    if (dayEnd) this.monthEndTarget.value = dayEnd.slice(0, 7)
  }

  parseDate(value) {
    if (!value) return null
    const [year, month, day] = value.split("-").map(Number)
    if (!year || !month || !day) return null
    return new Date(year, month - 1, day)
  }

  formatDate(date) {
    const y = date.getFullYear()
    const m = String(date.getMonth() + 1).padStart(2, "0")
    const d = String(date.getDate()).padStart(2, "0")
    return `${y}-${m}-${d}`
  }
}
